/**
 * PrestigePanel — HTML/CSS overlay for the Prestige (New Game+) system.
 *
 * Opens when the player presses P (or when debug 'prestige' command is used).
 * Shows: current prestige level, active bonus, and 3 new bonus choices.
 * Confirming a choice calls PrestigeSystem.doPrestige(selectedBonus).
 *
 * @module PrestigePanel
 */

import { UIPanel } from './UIPanel.js';
import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';

/**
 * @class PrestigePanel
 * @extends UIPanel
 */
class PrestigePanel extends UIPanel {
  constructor() {
    super('panel-prestige', { trapFocus: true, zLayer: 'modal' });
    this._selectedBonus = null;
    this._bonusOptions  = [];
    this._bindEvents();
    DebugLog.info('[prestige] PrestigePanel created');
  }

  // ── HTML template ──────────────────────────────────────────────────────────

  _buildHTML() {
    return `
      <div class="prestige-panel-inner">
        <header class="prestige-header">
          <div class="prestige-stars" id="prestige-stars"></div>
          <h2 class="prestige-title">PRESTIGE</h2>
          <p class="prestige-subtitle">A new saga begins — carry your legend forward.</p>
        </header>

        <section class="prestige-current" id="prestige-current">
          <p class="prestige-current-label">Current Prestige Level: <strong id="prestige-level-num">0</strong></p>
          <p class="prestige-active-bonus" id="prestige-active-bonus-text"></p>
        </section>

        <section class="prestige-choices" id="prestige-choices">
          <h3 class="prestige-choices-title">Choose Your Carry-Over Bonus</h3>
          <p class="prestige-choices-hint">Select one bonus. This will carry into your new run.</p>
          <div class="prestige-bonus-list" id="prestige-bonus-list"></div>
        </section>

        <footer class="prestige-footer">
          <button class="spellroads-btn prestige-btn-confirm" id="prestige-btn-confirm" disabled>
            Begin New Run
          </button>
          <button class="spellroads-btn spellroads-btn-secondary prestige-btn-cancel" id="prestige-btn-cancel">
            Not Yet
          </button>
        </footer>

        <p class="prestige-warning">
          WARNING: Confirming will reset the world. Your character levels and inventory will be lost.
          Only the chosen bonus carries forward.
        </p>
      </div>
    `;
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Open the prestige panel, populate current state and bonus choices.
   */
  open() {
    const ps = window.__prestigeSystem;
    if (!ps) {
      DebugLog.warn('[prestige] PrestigePanel.open: PrestigeSystem not available');
      return;
    }

    this._selectedBonus = null;
    this._bonusOptions  = ps.getAvailableBonuses();

    this._renderState(ps);
    this._renderBonuses();
    this._updateConfirmButton();
    this.show();

    DebugLog.info('[prestige] PrestigePanel opened');
  }

  // ── Render ────────────────────────────────────────────────────────────────

  _renderState(ps) {
    const level  = ps.getPrestigeLevel();
    const active = ps.getActiveBonus();

    // Star badges
    const starsEl = this.el.querySelector('#prestige-stars');
    if (starsEl) {
      starsEl.innerHTML = level > 0
        ? Array.from({ length: Math.min(level, 10) }, () => '<span class="prestige-star">★</span>').join('')
        : '<span class="prestige-star-none">No prestige yet</span>';
    }

    // Level number
    const levelNumEl = this.el.querySelector('#prestige-level-num');
    if (levelNumEl) levelNumEl.textContent = level;

    // Active bonus text
    const activeBonusEl = this.el.querySelector('#prestige-active-bonus-text');
    if (activeBonusEl) {
      activeBonusEl.textContent = active
        ? `Active bonus: ${active.name} — ${active.description}`
        : 'No active bonus.';
    }
  }

  _renderBonuses() {
    const listEl = this.el.querySelector('#prestige-bonus-list');
    if (!listEl) return;

    listEl.innerHTML = '';

    for (const bonus of this._bonusOptions) {
      const btn = document.createElement('button');
      btn.className = 'prestige-bonus-card spellroads-btn';
      btn.dataset.bonusId = bonus.id;
      btn.setAttribute('tabindex', '0');
      btn.innerHTML = `
        <span class="prestige-bonus-name">${this._escapeHtml(bonus.name)}</span>
        <span class="prestige-bonus-desc">${this._escapeHtml(bonus.description)}</span>
      `;

      btn.addEventListener('click', () => this._selectBonus(bonus));
      listEl.appendChild(btn);
    }
  }

  _selectBonus(bonus) {
    this._selectedBonus = bonus;

    // Highlight selected card
    const cards = this.el.querySelectorAll('.prestige-bonus-card');
    for (const card of cards) {
      card.classList.toggle('selected', card.dataset.bonusId === bonus.id);
    }

    this._updateConfirmButton();
    DebugLog.info(`[prestige] Bonus selected: ${bonus.id}`);
  }

  _updateConfirmButton() {
    const btn = this.el.querySelector('#prestige-btn-confirm');
    if (btn) btn.disabled = !this._selectedBonus;
  }

  // ── Events ────────────────────────────────────────────────────────────────

  _bindEvents() {
    EventBus.on('prestige:available', () => {
      // Show toast via the Toast system if available
      import('./Toast.js').then(({ default: Toast }) => {
        Toast.show('Prestige Available! Press P to choose your carry-over bonus.', 'gold', 6000);
      }).catch(() => {});
    });

    // Wire up confirm and cancel buttons after DOM is built
    // (buttons are created in _buildHTML so we delegate via the panel el)
    this.el.addEventListener('click', (e) => {
      if (e.target.id === 'prestige-btn-confirm' || e.target.closest('#prestige-btn-confirm')) {
        this._handleConfirm();
      }
      if (e.target.id === 'prestige-btn-cancel' || e.target.closest('#prestige-btn-cancel')) {
        this.hide();
      }
    });
  }

  _handleConfirm() {
    if (!this._selectedBonus) return;

    const ps = window.__prestigeSystem;
    if (!ps) return;

    const result = ps.doPrestige(this._selectedBonus);
    DebugLog.info(`[prestige] doPrestige result: ${result.message}`);

    this.hide();

    // Show result toast
    import('./Toast.js').then(({ default: Toast }) => {
      Toast.show(result.message, result.ok ? 'success' : 'error', 8000);
    }).catch(() => {});

    if (result.ok) {
      // Transition to main menu / world after a short delay
      setTimeout(() => {
        import('../core/SceneManager.js').then(m => m.default.transition('mainMenu'));
      }, 1500);
    }
  }

  // ── Utility ───────────────────────────────────────────────────────────────

  _escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

export default PrestigePanel;
